import { useRef, type ChangeEvent } from 'react';
import { Upload, Loader2, CheckCircle2 } from 'lucide-react';
import { Button, useToast } from '../../ui';

export interface IngestionPanelProps {
  ingesting: boolean;
  ingestStatus: string | null;
  /** Uploads a text file to the KB bucket and starts ingestion. */
  onIngestFile: (fileName: string, content: string) => Promise<void>;
}

/**
 * KB ingestion panel for the Chat page: upload a text file → UploadDocument to
 * the KB S3 bucket → StartIngestion, so the content becomes answerable in chat.
 *
 * Presentational only — data access flows through the injected callback (which
 * the useChat hook binds to the KB store).
 */
export function IngestionPanel({
  ingesting,
  ingestStatus,
  onIngestFile,
}: IngestionPanelProps) {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    try {
      const content = await file.text();
      await onIngestFile(file.name, content);
      toast({ tone: 'success', title: 'Uploaded & ingestion started', description: file.name });
    } catch (err) {
      toast({
        tone: 'error',
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Could not upload document.',
      });
    }
  };

  return (
    <div className="space-y-4 rounded-2xl border border-surface-border bg-surface-1 p-4">
      <div>
        <p className="text-sm font-medium text-zinc-200">Add to the knowledge base</p>
        <p className="mt-0.5 text-xs text-zinc-500">
          Upload a text document to index it into the knowledge base so it
          becomes answerable in chat.
        </p>
      </div>

      {/* Upload + ingest */}
      <div className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
          Upload document
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".txt,.md,.json,.csv,.log,text/*"
          className="hidden"
          onChange={(e) => void onFile(e)}
        />
        <Button
          variant="outline"
          size="sm"
          loading={ingesting}
          leftIcon={!ingesting ? <Upload className="h-4 w-4" /> : undefined}
          onClick={() => fileRef.current?.click()}
        >
          {ingesting ? 'Uploading…' : 'Choose a text file'}
        </Button>
        {ingestStatus && (
          <p className="flex items-start gap-1.5 text-xs text-zinc-400">
            {ingesting ? (
              <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
            ) : (
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
            )}
            <span className="break-all">{ingestStatus}</span>
          </p>
        )}
      </div>
    </div>
  );
}

export default IngestionPanel;
